import { ReactElement } from "react";
import { GridProps } from "@progress/kendo-react-grid/dist/npm/interfaces/GridProps";
import { GridItemChangeEvent, GridRowClickEvent, GridRowProps } from "@progress/kendo-react-grid";
import { DataResult } from "@progress/kendo-data-query";
import _ from "lodash";
import { EDIT_FIELD } from "../GridConstants";
import { useDebounce } from "../../Project/EditProject/UseDebounce";
import { InlineEditRowRender } from "./InlineEditRowRender";

const replaceItemByIndex = <T,>(data: Array<T>, index: number, newItem: T) =>
  data.map((x, i) => (i === index ? newItem : x));

interface Props {
  readonly idField: string;
  readonly dataResult: DataResult;
  readonly onDataReceived: (data: DataResult) => void;
  readonly setEditState: (rowNum: number | undefined) => void;
  readonly saveChanges?: <T>(changedItem: T) => Promise<T>;
  readonly isReadonly: boolean;
}

export default function InlineEditProps({
  idField,
  dataResult,
  onDataReceived,
  setEditState,
  saveChanges,
  isReadonly,
}: Props): GridProps {
  const updateItemDebounced = useDebounce(
    async (item: any, indexOfChangedItem: number, data: Array<any>, total: number) => {
      const newDataItem = await saveChanges?.(item);
      applyPessimisticUpdate(indexOfChangedItem, newDataItem, data, total);
    },
    1000
  );

  if (isReadonly) {
    return {};
  }

  const onRowClick = (event: GridRowClickEvent) => {
    const selectedId = _.get(event.dataItem, idField);
    setEditState(selectedId);
  };

  const onItemChange = async (event: GridItemChangeEvent) => {
    const nameOfChangedField = event.field || "";
    const indexOfChangedItem = findIndexByIdField(event.dataItem);

    const updatedItem = applyOptimisticUpdate(
      indexOfChangedItem,
      event.dataItem,
      nameOfChangedField,
      event.value
    );

    if (saveChanges) {
      // skip update on server if item has no id
      const id = _.get(event.dataItem, idField);
      if (!id) {
        return updatedItem;
      }

      updateItemDebounced(updatedItem, indexOfChangedItem, dataResult.data, dataResult.total);
    }

    return updatedItem;
  };

  const findIndexByIdField = <T,>(dataItem: T) =>
    dataResult.data.findIndex((x) => _.get(x, idField) === _.get(dataItem, idField));

  function applyOptimisticUpdate<T>(
    indexOfChangedItem: number,
    previousItem: T,
    nameOfChangedField: string,
    changedValue: any
  ): T {
    const updatedItem = {
      ...previousItem,
      [nameOfChangedField]: changedValue,
    };
    const updatedData = replaceItemByIndex(dataResult.data, indexOfChangedItem, updatedItem);
    onDataReceived({ data: updatedData, total: dataResult.total });
    return updatedItem;
  }

  function applyPessimisticUpdate(
    indexOfChangedItem: number,
    newDateItem: Promise<void> | undefined,
    data: Array<any>,
    total: number
  ): void {
    const newDataList = replaceItemByIndex(data, indexOfChangedItem, newDateItem);
    onDataReceived({ data: newDataList, total });
  }

  const exitEdit = (dataItem: any) => {
    setEditState(undefined);
  };

  const inlineEditRowRender = (tr: ReactElement<HTMLTableRowElement>, props: GridRowProps) => (
    <InlineEditRowRender originalProps={props} tr={tr} editField={EDIT_FIELD} exitEdit={exitEdit} />
  );

  return {
    onRowClick,
    onItemChange,
    editField: EDIT_FIELD,
    rowRender: inlineEditRowRender,
  };
}
